// ProjectFrame.tsx — the LIVE project row, rendered on the board.
//
// WHY THIS EXISTS (Erez, 15 Aug 2026). A project's state lived in two places:
// the `projects` row, which every agent's contract tells it to maintain, and a
// `kb-doc/projects/<slug>` page, which nothing did. The row was current; the
// page had been frozen since 25 July — on Falcon it still read "nothing
// deployed" six weeks after the client went live. Thirteen projects had the
// same split. He chose: the ROW is the truth, the pages go, and the board reads
// the row.
//
// So this is DocumentFrame's sibling with one difference — where DocumentFrame
// fetches a page_content row that is already blocks, this fetches a projects row
// whose `summary` is markdown and runs it through `mdToBlocks` first.
//
// THAT CONVERTER IS THE POINT, not a shortcut. The house rule is "no markdown
// rendering on data pages", and this does not break it: `mdToBlocks` turns
// markdown into the same BlockRenderer recipe every other page uses, and it is
// already shared with ProjectDetailPage, which renders exactly this field at
// /projects/<slug>. One converter, one render path, two surfaces — so the board
// cannot drift from the project page, because neither holds a copy.
//
// Everything else — the frame chrome, the React-Flow escape hatches, the
// caption strip — is DocumentFrame's, deliberately identical so the two read as
// one component on the canvas.

import { useEffect, useState } from "react";
import { BlockRenderer, type Block } from "../blueprint/BlockRenderer";
import { architectureConfig } from "../blueprint/ArchitectureBlocks";
import { mdToBlocks, bodyToBlocks } from "../blueprint/mdToBlocks";
import { useApi } from "../../../lib/api";

const FONT = "'Inter Tight', Inter, system-ui, -apple-system, sans-serif";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

export function ProjectFrame({
  slug,
  label,
  width = 860,
  height = 1180,
}: {
  /** projects.slug, e.g. "falcon-real-estate" — NOT a kb-doc path */
  slug: string;
  /** Small caption above the sheet. Falls back to the project's name. */
  label?: string;
  width?: number;
  /** Fixed frame height — the document scrolls inside it. */
  height?: number;
}) {
  const api = useApi();
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setBlocks(null);
    setError(null);
    (async () => {
      try {
        const res = await api(`/api/projects/${slug}`);
        if (!res.ok) {
          if (!cancelled) setError(`${res.status} — could not load project ${slug}`);
          return;
        }
        const p = (await res.json()) as {
          name?: string;
          summary?: string | null;
          status?: string | null;
          owner_agent?: string | null;
          updated_at?: string | null;
        };
        if (cancelled) return;
        setName(p.name ?? null);
        // An empty summary is a real state, not an error: a project can exist
        // before anyone has written its brain. Say so rather than showing a
        // blank sheet that reads as a broken frame.
        if (p.summary && p.summary.trim()) {
          const b = bodyToBlocks(p.summary) ?? mdToBlocks(p.summary);
          // A KB page's header is a centred Hero with an eyebrow ABOVE the
          // title and a sub-eyebrow under it. The converter cannot invent those
          // — they are not in the markdown and should not be, because they are
          // facts about the ROW rather than about the prose. So the frame fills
          // them from the row it just fetched: who owns it, what state it is in,
          // when it last moved. Real data, not decoration, and it is what makes
          // this read as a document rather than as a title on a page.
          const hero = b?.[0];
          if (hero && hero.type === "Hero") {
            const props = hero.props as Record<string, unknown>;
            const bits = [
              p.status ? String(p.status).toUpperCase() : null,
              p.owner_agent ? `OWNED BY ${String(p.owner_agent).toUpperCase()}` : null,
            ].filter(Boolean);
            props.eyebrow = props.eyebrow ?? "PROJECT SUMMARY";
            props.subEyebrow =
              props.subEyebrow ??
              [
                ...bits,
                p.updated_at
                  ? `UPDATED ${new Date(p.updated_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ");
          }
          setBlocks(b);
        }
        else setError(`project ${slug} has no summary yet`);
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
    // `api` is deliberately not a dependency — useApi() returns a fresh closure
    // every render, so including it re-runs this forever. Same note as
    // DocumentFrame, same reason.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const caption = label ?? name ?? slug;

  return (
    <div style={{ width, fontFamily: FONT, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, paddingInline: 4 }}>
        <div style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, letterSpacing: "0.14em", textTransform: "uppercase", color: "#8C948E" }}>
          {caption}
        </div>
        <a
          href={`/projects/${slug}`}
          className="nodrag nopan"
          style={{ pointerEvents: "auto", fontFamily: MONO, fontSize: 11, color: "#8C948E", textDecoration: "none", whiteSpace: "nowrap" }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          open ↗
        </a>
      </div>

      <div
        className="nowheel nodrag nopan"
        data-zoom
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          pointerEvents: "auto",
          width,
          height,
          overflowY: "auto",
          overflowX: "hidden",
          background: "#FFFFFF",
          border: "1px solid #E3DED4",
          borderRadius: 18,
          boxShadow: "0 10px 30px rgba(27,33,30,0.05)",
          padding: "44px 52px 56px",
          boxSizing: "border-box",
        }}
      >
        {error && <div style={{ fontFamily: MONO, fontSize: 13, color: "#C0492F" }}>{error}</div>}
        {!error && !blocks && <div style={{ fontFamily: MONO, fontSize: 13, color: "#8C948E" }}>loading…</div>}
        {blocks && <BlockRenderer config={architectureConfig} blocks={blocks} />}
      </div>
    </div>
  );
}
